"use client"

import { type Tone, cn, toneClass } from "../../lib/utils"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDownIcon } from "lucide-react"
import * as React from "react"

function Accordion({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root> & {
  // "flush": the footer of a card, its items as hairline rows (pair it with
  // `divider="soft"` items) edge to edge under a single top hairline; the card
  // clips the corners. Collapses away when no item renders.
  variant?: "flush"
}) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn(
        variant === "flush" && "border-border/70 border-t empty:hidden",
        className,
      )}
      {...props}
    />
  )
}

function AccordionItem({
  className,
  divider,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> & {
  divider?: "soft"
}) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        "border-b last:border-b-0",
        divider === "soft" && "border-border/70 border-0 border-b",
        className,
      )}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  tone,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & { tone?: Tone }) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
          toneClass(tone),
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  tone,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & { tone?: Tone }) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      {...props}
    >
      <div className={cn("pt-0 pb-4", toneClass(tone), className)}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
